<template>
  <div id="template-config">
    <template-options
      :dirty="dirty"
      @reload="reload()"
      @editStyle="editTemplateStyle()"
      @editSettings="enableSettingsForm = true"
      @addLine="newLine()"
      @update="saveTemplate()"
      @fetchDefault="fetchDefaultTemplate()"
      @deleteTemplate="deleteTemplate()"
    />

    <template-setting
      :show.sync="enableSettingsForm"
      :settings="templateSetting"
      :template-masters="masters"
      @updateSettings="updateSettings"
    />

    <empty-template
      :show="!hasTemplateLines"
      @fetch="fetchDefaultTemplate()"
    />

    <v-row
      v-if="hasTemplateLines"
      class="pa-4"
    >
      <v-col
        xs="12"
        sm="8"
      >
        <template-lines
          :lines="templateLines"
          @editLine="editLine"
          @lineChange="updateLines"
          @editStyle="editStyle"
          @removeLine="removeLine"
          @editSettings="editSettings"
        />
      </v-col>

      <v-col cols="4">
        <print-view :lines="templateLines" />
      </v-col>
    </v-row>

    <line-form
      :show-dialog.sync="enableLineForm"
      :line-data="currentLine"
      :template-masters="masters"
      :is-new="isNewLine"
      @save="updateLine"
    />
    <style-config
      :style-config="currentStyleInfo"
      :show-style.sync="enableStyleForm"
      :template-masters="masters"
      @updateStyle="updateStyle"
    />
    <component-setting
      :show.sync="enableComponentSettingsForm"
      :data="selectedComp"
      @updateSettings="updateComponentSettings"
    />
  </div>
</template>

<script>
import { initTemplate, newLine, masterIns } from "./templateHelper/Helper.js";
import LineForm from "./LineForm";
import TemplateOptions from "./TemplateOptions";
import EmptyTemplate from "./EmptyTemplate";
import TemplateLines from "./TemplateLines";
import TemplateSetting from "./TemplateSetting";
import StyleConfig from "./Styles";
import PrintView from "./PrintView";
import ComponentSetting from "./settings/Settings";

export default {
  name: "TemplateSettings",
  components: {
    LineForm,
    TemplateOptions,
    EmptyTemplate,
    TemplateLines,
    TemplateSetting,
    StyleConfig,
    PrintView,
    ComponentSetting
  },
  props: {
    templateType: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      storeId: null,
      masters: masterIns(),
      // dirty : flag used to identify the changes done not save
      dirty: false,

      // currentStyleInfo capture the style of a component or a template ,
      // used to edit form
      currentStyleInfo: {},
      // enableStyleForm : enable style edit from dialog
      enableStyleForm: false,

      // enableSettingForm : enable settings form dialog
      enableSettingsForm: false,
      settings: {},

      // line create / edit form
      enableLineForm: false,
      currentLine: newLine(),
      isNewLine: false,

      // template data
      templateSettings: null,

      // enableComponentSettingsForm: enable/disable component settings form
      enableComponentSettingsForm: false,
      selectedComp: null
    };
  },
  computed: {
    templateLines() {
      if (!this.templateSettings) {
        return [];
      }
      return (
        JSON.parse(JSON.stringify(this.templateSettings.templateLines)) || []
      );
    },
    templateSetting() {
      if (!this.templateSettings) {
        return {};
      }
      return JSON.parse(JSON.stringify(this.templateSettings?.settings));
    },
    hasTemplateLines() {
      return this.templateLines.length > 0;
    }
  },
  watch: {
    templateType: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.init();
        }
      }
    }
  },
  methods: {
    fetchStoreTemplateMasters() {
      this.$http
        .get(
          `${this.ADMIN_API_URL}/printerTemplateMasters/${this.templateType}`
        )
        .then((resp) => {
          this.masters = resp.body;
        });
    },

    setDirty(val = true) {
      this.dirty = val;
    },
    reload() {
      this.setDirty(false);
      this.fetchStoreTemplates();
    },
    reInitTemplate(template) {
      this.setDirty(false);
      this.templateSettings = initTemplate(template);
    },

    fetchStoreTemplates() {
      const url = `${this.ADMIN_API_URL}/stores/${this.storeId}/printerTemplates/${this.templateType}`;
      this.$http.get(url).then(
        (resp) => {
          this.reInitTemplate(resp.body);
        },
        (failure) => {
          this.templateSettings = null;
          this.handleFailure(failure);
        }
      );
    },

    fetchDefaultTemplate() {
      const url = `${this.ADMIN_API_URL}/stores/${this.storeId}/printerTemplates/${this.templateType}/tmp`;
      this.$http.get(url).then((resp) => {
        this.reInitTemplate(resp.body);
        this.setDirty();
      });
    },

    editTemplateStyle() {
      this.editStyle(this.templateSettings.settings.style);
    },
    editStyle(style, refId = null) {
      this.currentStyleInfo = {
        refId: refId,
        style: JSON.parse(JSON.stringify(style))
      };
      this.enableStyleForm = true;
    },
    updateStyle({ refId = null, style }) {
      this.setDirty();
      if (!refId) {
        this.templateSettings.settings.style = style;
        this.enableStyleForm = false;
        return;
      }
      this.templateSettings.templateLines.forEach((line) => {
        line.components.some((c) => {
          if (c.refId === refId) {
            c.style = style;
            return true;
          }
          return false;
        });
      });
      this.enableStyleForm = false;
    },

    updateSettings(settings) {
      this.setDirty();
      this.templateSettings.settings = settings;
      this.enableSettingsForm = false;
    },

    updateLines(lines) {
      this.templateSettings.templateLines = lines;
      this.setDirty();
    },

    newLine() {
      this.editLine(newLine(), true);
    },
    editLine(line, isNewLine = false) {
      this.currentLine = JSON.parse(JSON.stringify(line));
      this.enableLineForm = true;
      this.isNewLine = isNewLine;
    },
    updateLine({ lineIns, isNewLine }) {
      const refId = lineIns.refId;
      this.setDirty();
      if (isNewLine) {
        let newIdx = lineIns.number - 1;
        this.templateSettings.templateLines.splice(newIdx, 0, lineIns);
        this.enableLineForm = false;
        return;
      }

      this.templateSettings.templateLines.some((tl) => {
        if (tl.refId === refId) {
          tl.number = lineIns.number;
          tl.components = lineIns.components;
          return true;
        }
        return false;
      });
      this.enableLineForm = false;
    },
    removeLine({ refId }) {
      this.templateSettings.templateLines.some((line, idx) => {
        if (line.refId === refId) {
          this.templateSettings.templateLines.splice(idx, 1);
          this.setDirty();
          return true;
        }
        return false;
      });
    },

    editSettings(comp) {
      this.selectedComp = JSON.parse(JSON.stringify(comp));
      this.enableComponentSettingsForm = true;
    },
    updateComponentSettings({ refId, settingsIns }) {
      this.templateSettings.templateLines.some((line) => {
        let found = false;
        line.components.some((c) => {
          if (c.refId === refId) {
            this.$set(c, "settings", settingsIns);
            this.setDirty();
            found = true;
            return found;
          }
        });
        return found;
      });
    },

    saveTemplate() {
      this.templateSettings.templateLines.forEach((line, idx) => {
        line.number = idx + 1;
      });
      this.templateSettings.templateType = this.templateType;
      let url = `${this.ADMIN_API_URL}/stores/${this.storeId}/printerTemplates/${this.templateType}`;
      this.$http.put(url, this.templateSettings).then(
        (resp) => {
          this.handleSuccess(resp);
        },
        (failure) => {
          this.handleFailure(failure);
        }
      );
    },

    /** delete the template from database */
    deleteTemplate() {
      const url = `${this.ADMIN_API_URL}/stores/${this.storeId}/printerTemplates/${this.templateType}`;
      this.$http.delete(url).then(
        (resp) => {
          this.templateSettings = null;
          this.$store.commit("showSnackbar", {
            timeout: 3000,
            message: resp.body.message
          });
        },
        (failure) => {
          this.handleFailure(failure);
        }
      );
    },

    handleSuccess: function (response) {
      this.$store.commit("showSnackbar", {
        timeout: 3000,
        message: response.body.message
      });
      this.fetchStoreTemplates();
    },

    handleFailure: function (failure) {
      let msg = failure.body.message || "Error";
      this.$store.commit("showSnackbar", {
        message: msg,
        color: "error"
      });
    },

    init() {
      this.storeId = this.$route.params.storeId;
      this.fetchStoreTemplateMasters();
      this.fetchStoreTemplates();
    }
  }
};
</script>